<template>
  <v-main>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>
            <v-icon left>mdi-lan</v-icon>
            Network Configuration
          </v-card-title>
          <v-card-text>
            <v-radio-group v-model="appMode" @change="onModeChange">
              <v-radio value="master">
                <template v-slot:label>
                  <div>
                    <strong>Master Mode</strong>
                    <span class="text-caption grey--text ml-2">This system hosts the database</span>
                  </div>
                </template>
              </v-radio>
              <v-radio value="client">
                <template v-slot:label>
                  <div>
                    <strong>Client Mode</strong>
                    <span class="text-caption grey--text ml-2">Connect to another system</span>
                  </div>
                </template>
              </v-radio>
            </v-radio-group>

            <div v-if="appMode === 'client'" class="mt-4">
              <v-text-field
                v-model="masterIP"
                label="Master System IP Address"
                placeholder="192.168.1.10"
                :rules="[rules.required, rules.ip]"
                outlined
                dense
              ></v-text-field>
              <v-btn color="secondary" @click="testConnection" :loading="testing" class="mr-2">
                <v-icon left>mdi-connection</v-icon>
                Test Connection
              </v-btn>
              <v-alert v-if="testResult" :type="testResult.success ? 'success' : 'error'" class="mt-3" dense>
                {{ testResult.message }}
              </v-alert>
            </div>

            <div v-if="appMode === 'master'" class="mt-4">
              <v-alert type="info" outlined>
                <strong>Your IP Addresses:</strong>
                <div v-if="serverAddresses.length > 0" class="mt-2">
                  <div v-for="addr in serverAddresses" :key="addr.address" class="mb-1">
                    <v-chip small outlined>{{ addr.interface }}</v-chip>
                    <strong class="ml-2">{{ addr.address }}</strong>
                  </div>
                </div>
                <div v-else class="mt-2 text-caption">Loading IP addresses...</div>
                <div class="mt-2 text-caption">
                  Other systems should connect to one of these addresses on port {{ expressPort }}.
                </div>
              </v-alert>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="saveAndRestart" :disabled="!canSave" :loading="saving">
              <v-icon left>mdi-restart</v-icon>
              Save & Restart App
            </v-btn>
          </v-card-actions>
        </v-card>

        <!-- Online Sync Section (Master mode only) -->
        <v-card class="mt-4" v-if="appMode === 'master'">
          <v-card-title>
            <v-icon left>mdi-cloud-sync</v-icon>
            Online Backup Sync
          </v-card-title>
          <v-card-text>
            <v-row align="center">
              <v-col cols="auto">
                <v-chip :color="isOnline ? 'green' : 'red'" dark small>
                  <v-icon left small>{{ isOnline ? 'mdi-wifi' : 'mdi-wifi-off' }}</v-icon>
                  {{ isOnline ? 'Online' : 'Offline' }}
                </v-chip>
              </v-col>
              <v-col>
                <div class="text-caption">
                  Pending: {{ syncStats.pending }} |
                  Synced: {{ syncStats.synced }} |
                  <span :class="syncStats.failed > 0 ? 'orange--text font-weight-bold' : ''">
                    Failed: {{ syncStats.failed }}
                  </span> |
                  <span :class="syncStats.stranded > 0 ? 'red--text font-weight-bold' : ''">
                    Stuck: {{ syncStats.stranded || 0 }}
                  </span> |
                  <span :class="syncStats.conflict > 0 ? 'red--text font-weight-bold' : ''">
                    Conflicts: {{ syncStats.conflict || 0 }}
                  </span>
                </div>
              </v-col>
            </v-row>

            <v-progress-linear v-if="isSyncing" indeterminate color="primary" class="mt-3"></v-progress-linear>

            <div class="mt-3">
              <v-btn
                color="primary"
                @click="pullFromOnline"
                :disabled="!isOnline || isSyncing || syncStats.pending > 0"
                class="mr-2"
              >
                <v-icon left>mdi-cloud-download</v-icon>
                Pull from Online
              </v-btn>
              <v-btn
                color="secondary"
                @click="syncNow"
                :disabled="!isOnline || pushableCount === 0 || isSyncing"
                class="mr-2"
              >
                <v-icon left>mdi-cloud-upload</v-icon>
                Push to Online ({{ pushableCount }})
              </v-btn>
              <v-btn
                color="warning"
                @click="retrySyncFailed"
                :disabled="(syncStats.failed + (syncStats.stranded || 0)) === 0 || isSyncing"
                outlined
              >
                <v-icon left>mdi-refresh</v-icon>
                Retry Failed ({{ syncStats.failed + (syncStats.stranded || 0) }})
              </v-btn>
            </div>

            <div
              v-if="syncStats.pending > 0"
              class="text-caption orange--text mt-2"
            >
              <v-icon x-small color="orange">mdi-information</v-icon>
              Push your {{ syncStats.pending }} pending change(s) to online before pulling.
              Items that already failed to push won't block you.
            </div>

            <div
              v-if="reconcileCount.total > 0"
              class="text-caption blue--text mt-2"
            >
              <v-icon x-small color="blue">mdi-cloud-upload-outline</v-icon>
              <strong>{{ reconcileCount.candidates }}</strong> candidate(s) and
              <strong>{{ reconcileCount.completions }}</strong> completion(s) still need to reach online —
              click <strong>Push to Online</strong> (repeat until this reaches 0).
            </div>

            <v-alert
              v-if="syncMessage"
              :type="syncMessage.type"
              class="mt-3"
              dense
              dismissible
              style="white-space: pre-line"
              @input="syncMessage = null"
            >
              {{ syncMessage.text }}
            </v-alert>

            <!-- Failed items: human-readable, grouped by person -->
            <v-card v-if="failedItems.length > 0" outlined class="mt-3 sync-failed">
              <v-card-text>
                <div class="sync-failed__head">
                  <v-icon color="#2E8B6F" small>mdi-check-circle</v-icon>
                  <span class="sync-failed__ok">Everyone else's data was sent successfully.</span>
                </div>
                <div class="sync-failed__sub">
                  {{ failedItems.length }} item(s) for {{ failedGroups.length }} account(s) couldn't be sent — here's why:
                </div>

                <div v-for="g in failedGroups" :key="g.key" class="sync-failed__person">
                  <div class="sync-failed__person-top">
                    <v-icon :color="g.reasonCode === 'account_deleted' ? '#C0453B' : '#D69A34'" small>
                      {{ g.reasonCode === 'account_deleted' ? 'mdi-account-off-outline' : 'mdi-account-alert-outline' }}
                    </v-icon>
                    <span class="sync-failed__name">{{ g.name || g.email || ('Item ' + g.key) }}</span>
                    <span v-if="g.email && g.name" class="sync-failed__email">{{ g.email }}</span>
                    <v-chip x-small outlined color="#C0453B" class="ml-auto">{{ g.count }} not sent</v-chip>
                  </div>
                  <div class="sync-failed__reason">{{ g.friendly || g.rawError }}</div>
                  <div v-if="g.courses.length" class="sync-failed__courses">
                    {{ g.courses.slice(0, 6).join(' · ') }}<span v-if="g.courses.length > 6"> +{{ g.courses.length - 6 }} more</span>
                  </div>
                </div>
              </v-card-text>
            </v-card>

            <!-- Sync Conflicts -->
            <v-card v-if="conflicts.length > 0" outlined class="mt-3">
              <v-card-title class="text-subtitle-1 red--text">
                <v-icon left color="red">mdi-alert-circle</v-icon>
                {{ conflicts.length }} conflict(s) — server changed since last pull
              </v-card-title>
              <v-card-text>
                <div class="text-caption mb-2">
                  Local changes are preserved on this device but were NOT pushed because
                  another system has newer data on the server. Choose per item:
                </div>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th>Item</th><th>Reason</th><th style="width:260px">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="c in conflicts" :key="c.id">
                      <td>{{ c.table_name }}#{{ c.record_id }} ({{ c.operation }})</td>
                      <td class="text-caption">{{ c.error_message }}</td>
                      <td>
                        <v-btn x-small color="warning" @click="forcePush(c.id)" class="mr-1">
                          Force push (keep local)
                        </v-btn>
                        <v-btn x-small color="grey" outlined @click="discardLocal(c.id)">
                          Discard (keep server)
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-card-text>
            </v-card>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "NetworkSettings",
  data() {
    return {
      appMode: "master",
      masterIP: "",
      masterPort: 8080,
      testing: false,
      testResult: null,
      saving: false,
      serverAddresses: [],
      expressPort: 8080,
      isOnline: navigator.onLine,
      syncStats: { pending: 0, synced: 0, failed: 0, stranded: 0, conflict: 0 },
      reconcileCount: { candidates: 0, completions: 0, total: 0 },
      isSyncing: false,
      syncMessage: null,
      conflicts: [],
      failedItems: [],
      rules: {
        required: (v) => !!v || "Required",
        ip: (v) => /^(\d{1,3}\.){3}\d{1,3}$/.test(v) || "Invalid IP format",
      },
    };
  },
  computed: {
    canSave() {
      if (this.appMode === "master") return true;
      return this.masterIP && this.rules.ip(this.masterIP) === true;
    },
    // Everything a push can actually send: queued (pending + retryable failed) PLUS the
    // reconcile work (candidates/completions not yet on the server). This lets the Push
    // button enable even when the queue is empty but there's still data to send online.
    pushableCount() {
      return (this.syncStats.pending || 0) + (this.syncStats.failed || 0) + (this.reconcileCount.total || 0);
    },
    // Group failed items by person so the admin sees WHO couldn't be sent and WHY,
    // instead of a list of "course_candidates#24961" rows.
    failedGroups() {
      const groups = {};
      for (const f of this.failedItems) {
        const key = f.personEmail || (f.table_name + "#" + f.record_id);
        if (!groups[key]) {
          groups[key] = {
            key,
            name: f.personName,
            email: f.personEmail,
            friendly: f.friendly,
            reasonCode: f.reasonCode,
            rawError: f.error_message,
            count: 0,
            courses: [],
          };
        }
        const g = groups[key];
        g.count++;
        if (f.courseName) g.courses.push(f.courseName);
        if (!g.friendly && f.friendly) { g.friendly = f.friendly; g.reasonCode = f.reasonCode; }
      }
      return Object.values(groups);
    },
  },
  async mounted() {
    // Load current config
    if (window.electron) {
      try {
        const config = await window.electron.getAppConfig();
        this.appMode = config.app_mode || "master";
        this.masterIP = config.master_ip || "";

        if (this.appMode === "master") {
          this.loadServerInfo();
          this.loadSyncStats();
        }
      } catch (e) {
        console.error("Failed to load app config:", e);
      }
    }

    // Listen for online/offline events
    window.addEventListener("online", () => {
      this.isOnline = true;
    });
    window.addEventListener("offline", () => {
      this.isOnline = false;
    });
  },
  methods: {
    async loadServerInfo() {
      try {
        // Note: axios baseURL already includes /api, so use /server-info not /api/server-info
        const res = await axios.get("/server-info");
        this.serverAddresses = res.data.addresses || [];
        this.expressPort = res.data.port || 8080;
      } catch (e) {
        console.error("Failed to load server info:", e);
      }
      // Also try to get port from electron API
      if (window.electron && window.electron.getExpressPort) {
        try {
          const port = await window.electron.getExpressPort();
          if (port) this.expressPort = port;
        } catch (e) {
          console.error("Failed to get express port:", e);
        }
      }
    },
    async pullFromOnline() {
      this.isSyncing = true;
      this.syncMessage = null;

      try {
        if (window.electron && window.electron.syncFromOnline) {
          const result = await window.electron.syncFromOnline();
          if (result.success) {
            this.syncMessage = {
              type: "success",
              text: `Pulled from online — courses: ${result.results.courses}, users: ${result.results.users}, companies: ${result.results.companies}, assignments: ${result.results.course_candidates}, exams: ${result.results.course_exams}`,
            };
          } else if (result.requiresPush) {
            this.syncMessage = {
              type: "warning",
              text: result.error || "Push your pending changes to online before pulling.",
            };
          } else {
            this.syncMessage = {
              type: "error",
              text: "Sync failed: " + (result.error || "Unknown error"),
            };
          }
        } else {
          this.syncMessage = {
            type: "error",
            text: "Online sync not available",
          };
        }
      } catch (e) {
        this.syncMessage = {
          type: "error",
          text: "Sync failed: " + e.message,
        };
      }

      this.isSyncing = false;
    },
    async loadSyncStats() {
      try {
        const res = await axios.get("/sync/queue");
        this.syncStats = res.data.data || { pending: 0, synced: 0, failed: 0, stranded: 0, conflict: 0 };
      } catch (e) {
        console.error("Failed to load sync stats:", e);
      }
      // How many candidates/completions still need to reach the server (reconcile work).
      try {
        const rRes = await axios.get("/sync/reconcile-count");
        if (rRes.data && rRes.data.success) {
          this.reconcileCount = { candidates: rRes.data.candidates || 0, completions: rRes.data.completions || 0, total: rRes.data.total || 0 };
        }
      } catch (e) {
        // endpoint may not exist on older clients — ignore
      }
      try {
        const cRes = await axios.get("/sync/conflicts");
        this.conflicts = (cRes.data && cRes.data.data) || [];
      } catch (e) {
        // endpoint may not exist on older clients — ignore
      }
      try {
        const fRes = await axios.get("/sync/failed");
        this.failedItems = (fRes.data && fRes.data.data) || [];
      } catch (e) {
        // endpoint may not exist on older clients — ignore
      }
    },
    async forcePush(id) {
      try {
        await axios.post(`/sync/conflicts/${id}/force`);
        this.syncMessage = { type: "info", text: "Queued for force-push. Click 'Push to Online' to send." };
        await this.loadSyncStats();
      } catch (e) {
        this.syncMessage = { type: "error", text: "Force-push failed: " + e.message };
      }
    },
    async discardLocal(id) {
      try {
        await axios.post(`/sync/conflicts/${id}/discard`);
        this.syncMessage = { type: "info", text: "Local change discarded; pulled fresh from server." };
        await this.loadSyncStats();
      } catch (e) {
        this.syncMessage = { type: "error", text: "Discard failed: " + e.message };
      }
    },
    async testConnection() {
      this.testing = true;
      this.testResult = null;

      try {
        if (window.electron) {
          const result = await window.electron.testConnection(this.masterIP);
          if (result.success && result.port) {
            this.masterPort = result.port;
            console.log(`[NETWORK] Master found on port ${result.port}`);
          }
          this.testResult = {
            success: result.success,
            message: result.success
              ? `Connected to ${result.data?.hostname || this.masterIP} on port ${result.port || 8080}`
              : `Connection failed: ${result.error}`,
          };
        }
      } catch (e) {
        this.testResult = { success: false, message: e.message };
      }

      this.testing = false;
    },
    onModeChange() {
      this.testResult = null;
      if (this.appMode === "master") {
        this.loadServerInfo();
        this.loadSyncStats();
      }
    },
    async saveAndRestart() {
      this.saving = true;

      try {
        if (window.electron) {
          await window.electron.setAppMode(this.appMode);
          if (this.appMode === "client") {
            await window.electron.setMasterIP(this.masterIP);
          }

          // Also save to localStorage for axios-offline.js
          localStorage.setItem("app_mode", this.appMode);
          localStorage.setItem("master_ip", this.masterIP);
          if (this.appMode === "client") {
            localStorage.setItem("master_port", this.masterPort.toString());
            console.log(`[NETWORK] Saved master_port: ${this.masterPort}`);
          }

          // Reload app
          window.location.reload();
        }
      } catch (e) {
        console.error("Failed to save settings:", e);
        this.saving = false;
      }
    },
    async syncNow() {
      this.isSyncing = true;
      this.syncMessage = null;

      // ONE click does everything: repeat the batched push automatically until nothing
      // is left to send. A "no-progress" guard stops it if some items genuinely can't go.
      let totalSynced = 0, totalCreated = 0, totalPushed = 0, totalLinked = 0, totalVessel = 0;
      const MAX_ITER = 500;
      let prevRemaining = Infinity;
      try {
        for (let i = 0; i < MAX_ITER; i++) {
          const res = await axios.post("/sync/push");
          const d = res.data || {};
          const syncedNow = d.synced || 0;
          const createdNow = d.reconcileCreated || 0;
          const pushedNow = d.reconcilePushed || 0;
          // Linking an already-online account/completion IS progress (the item drops out of
          // the "needs online" set) even though nothing was created or pushed. The reconcile
          // works in bounded batches (MAX_OPS per call), so a batch may only link — we must
          // NOT treat that as "stuck", or the loop quits early leaving items unsent.
          const linkedNow = d.reconcileLinked || 0;
          // Setting an account's vessel online is ALSO progress (it drops out of the
          // needs-work set), even if nothing else moved — count it so a vessel-only batch
          // is not mistaken for "stuck".
          const vesselNow = d.reconcileVesselFixed || 0;
          const remaining = (d.remaining || 0) + (d.reconcileRemaining || 0);
          totalSynced += syncedNow; totalCreated += createdNow; totalPushed += pushedNow; totalLinked += linkedNow; totalVessel += vesselNow;

          if (d.aborted) {
            this.syncMessage = { type: "error", text: `Stopped: server unreachable. Created ${totalCreated}, pushed ${totalPushed}. ${remaining} left — check connection and push again.` };
            break;
          }
          if (remaining === 0) {
            this.syncMessage = { type: "success", text: `Done! Everything is online — created ${totalCreated} account(s), pushed ${totalPushed} completion(s), linked ${totalLinked}, set ${totalVessel} vessel(s), synced ${totalSynced} change(s).` };
            break;
          }
          // Progress this round = created / pushed / linked / vessel-set something, OR the
          // remaining count actually went down. Only if a full round moved NOTHING is it stuck.
          const movedThisRound = (syncedNow + createdNow + pushedNow + linkedNow + vesselNow) > 0 || remaining < prevRemaining;
          if (!movedThisRound) {
            this.syncMessage = { type: "warning", text: `Sent what it could — created ${totalCreated} account(s), pushed ${totalPushed} completion(s), linked ${totalLinked}, set ${totalVessel} vessel(s). ${remaining} item(s) couldn't be sent (please tell support).` };
            break;
          }
          prevRemaining = remaining;
          // Otherwise keep going — show live progress.
          this.syncMessage = { type: "info", text: `Working… created ${totalCreated} account(s), pushed ${totalPushed} completion(s), linked ${totalLinked}, set ${totalVessel} vessel(s) so far — ${remaining} left…` };
        }
        this.loadSyncStats();
      } catch (e) {
        this.syncMessage = {
          type: "error",
          text: "Sync failed: " + ((e.response && e.response.data && e.response.data.message) || e.message),
        };
      }

      this.isSyncing = false;
    },
    async retrySyncFailed() {
      this.isSyncing = true;

      try {
        await axios.post("/sync/retry");
        await this.syncNow();
      } catch (e) {
        this.syncMessage = {
          type: "error",
          text: "Retry failed: " + ((e.response && e.response.data && e.response.data.message) || e.message),
        };
        this.isSyncing = false;
      }
    },
  },
};
</script>

<style scoped>
.sync-failed { border: 1px solid #f0d9b0 !important; border-radius: 12px !important; background: #fffdf8; }
.sync-failed__head { display: flex; align-items: center; gap: 8px; }
.sync-failed__ok { color: #2E8B6F; font-weight: 700; font-size: 0.95rem; }
.sync-failed__sub { color: #8a6d3b; font-size: 0.84rem; margin: 4px 0 14px; }

.sync-failed__person {
  border: 1px solid #eadfc7;
  border-radius: 10px;
  background: #ffffff;
  padding: 12px 14px;
  margin-bottom: 10px;
}
.sync-failed__person-top { display: flex; align-items: center; gap: 8px; }
.sync-failed__name { font-weight: 700; color: #0c3a5b; }
.sync-failed__email { color: #61798a; font-size: 0.82rem; }
.sync-failed__reason { color: #132430; font-size: 0.88rem; margin-top: 6px; line-height: 1.4; }
.sync-failed__courses { color: #8397a5; font-size: 0.78rem; margin-top: 6px; }
.ml-auto { margin-left: auto; }
</style>
